'use client';

import { Button } from './Button';
import { Modal } from './Modal';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';

// Promise-returning replacement for window.confirm / window.prompt. Call
// openDialog(opts) — or the confirmDialog wrapper — from any handler; the
// host (mounted once in the root layout) shows a themed modal and resolves
// the promise with the user's choice. If two requests overlap, the prior
// one resolves to null.

export type DialogFieldType = 'text' | 'textarea';

export interface DialogField {
  name: string;
  label: string;
  type?: DialogFieldType;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  rows?: number;
}

export type DialogButtonVariant = 'primary' | 'danger' | 'ghost' | 'default';

export interface DialogButton<TId extends string = string> {
  id: TId;
  label: string;
  variant?: DialogButtonVariant;
  submit?: boolean;
  autoFocus?: boolean;
}

export interface DialogOptions<TId extends string = string> {
  title: ReactNode;
  body?: ReactNode;
  fields?: DialogField[];
  buttons: DialogButton<TId>[];
}

export interface DialogResult<TId extends string = string> {
  buttonId: TId;
  values: Record<string, string>;
}

interface Pending {
  opts: DialogOptions<string>;
  resolve: (r: DialogResult<string> | null) => void;
}

let publish: ((p: Pending | null) => void) | null = null;

export function openDialog<TId extends string>(
  opts: DialogOptions<TId>,
): Promise<DialogResult<TId> | null> {
  return new Promise(resolve => {
    if (!publish) {
      resolve(null);
      return;
    }
    publish({
      opts: opts as DialogOptions<string>,
      resolve: r => resolve(r as DialogResult<TId> | null),
    });
  });
}

export interface ConfirmDialogOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'destructive' | 'default';
}

export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return openDialog({
    title: opts.title,
    body: opts.body,
    buttons: [
      { id: 'cancel', label: opts.cancelLabel ?? 'Cancel', variant: 'ghost' },
      {
        id: 'confirm',
        label: opts.confirmLabel ?? 'Confirm',
        variant: opts.tone === 'destructive' ? 'danger' : 'primary',
        submit: true,
        autoFocus: true,
      },
    ],
  }).then(r => r?.buttonId === 'confirm');
}

function seedValues(fields: DialogField[] | undefined): Record<string, string> {
  if (!fields) return {};
  return fields.reduce<Record<string, string>>((acc, f) => {
    acc[f.name] = f.defaultValue ?? '';
    return acc;
  }, {});
}

function findInvalid(fields: DialogField[] | undefined, values: Record<string, string>): string[] {
  if (!fields) return [];
  return fields.filter(f => f.required && values[f.name].trim() === '').map(f => f.name);
}

export const ConfirmDialogHost: FunctionComponent = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [invalid, setInvalid] = useState<string[]>([]);

  useEffect(() => {
    publish = next => {
      setPending(prev => {
        if (prev) prev.resolve(null);
        setValues(seedValues(next?.opts.fields));
        setInvalid([]);
        return next;
      });
    };
    return () => {
      publish = null;
    };
  }, []);

  if (!pending) return null;

  const { opts } = pending;

  const close = (result: DialogResult<string> | null): void => {
    pending.resolve(result);
    setPending(null);
  };

  const onButtonClick = (btn: DialogButton<string>): void => {
    if (btn.submit) {
      const bad = findInvalid(opts.fields, values);
      if (bad.length > 0) {
        setInvalid(bad);
        return;
      }
    }
    close({ buttonId: btn.id, values });
  };

  const onFieldChange = (name: string, value: string): void => {
    setValues(v => ({ ...v, [name]: value }));
    if (invalid.includes(name) && value.trim() !== '') {
      setInvalid(prev => prev.filter(n => n !== name));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    const submitBtn = opts.buttons.find(b => b.submit);
    if (!submitBtn) return;
    e.preventDefault();
    onButtonClick(submitBtn);
  };

  return (
    <Modal open onClose={() => close(null)} title={opts.title} size="sm">
      <div className="p-[20px] flex flex-col gap-[16px]" onKeyDown={onKeyDown}>
        {opts.body ? <div className="text-[13px] text-ink-2 leading-[1.55]">{opts.body}</div> : null}

        {opts.fields ? (
          <div className="flex flex-col gap-[12px]">
            {opts.fields.map((f, idx) => (
              <DialogFieldRow
                key={f.name}
                field={f}
                value={values[f.name] ?? ''}
                invalid={invalid.includes(f.name)}
                autoFocus={f.autoFocus ?? idx === 0}
                onChange={v => onFieldChange(f.name, v)}
              />
            ))}
          </div>
        ) : null}

        <div className="flex justify-end gap-[8px]">
          {opts.buttons.map(btn => (
            <Button
              key={btn.id}
              variant={btn.variant ?? 'default'}
              onClick={() => onButtonClick(btn)}
              autoFocus={btn.autoFocus && !opts.fields}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
    </Modal>
  );
};

interface DialogFieldRowProps {
  field: DialogField;
  value: string;
  invalid: boolean;
  autoFocus: boolean;
  onChange: (v: string) => void;
}

const DialogFieldRow: FunctionComponent<DialogFieldRowProps> = ({
  field,
  value,
  invalid,
  autoFocus,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if ('select' in el) el.select();
  }, [autoFocus]);

  const border = invalid ? 'border-rose focus:border-rose' : 'border-line-2 focus:border-acc';
  const sharedClass = `w-full px-[10px] py-[7px] text-[13px] bg-bg text-ink rounded-[var(--radius-sm)] border outline-none transition-colors ${border}`;

  return (
    <label className="flex flex-col gap-[4px]">
      <span className="text-[11.5px] text-ink-3 uppercase tracking-[0.04em]">
        {field.label}
        {field.required ? <span className="text-rose ml-1">*</span> : null}
      </span>
      {field.type === 'textarea' ? (
        <textarea
          ref={el => {
            inputRef.current = el;
          }}
          className={`${sharedClass} font-mono text-[12px] leading-[1.55] resize-y min-h-[80px]`}
          rows={field.rows ?? 5}
          placeholder={field.placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <input
          ref={el => {
            inputRef.current = el;
          }}
          type="text"
          className={sharedClass}
          placeholder={field.placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
      {invalid ? <span className="text-[11px] text-rose">Required</span> : null}
    </label>
  );
};
